module Meevo.Plugins.MembershipManager {
    "use strict";
    import ClientMembershipBillStatusEnum = Meevo.Model.ClientMembershipBillStatusEnum;
    import ProcessorResponseCodeEnum = Meevo.Model.ProcessorResponseCodeEnum;
    import ClientMembershipStatusEnum = Meevo.Model.ClientMembershipStatusEnum;
    import ClientMembershipIndicatorStatusEnum = Meevo.Model.ClientMembershipIndicatorStatusEnum;
    import FreezeSettingEnum = Meevo.Model.FreezeSettingEnum;
    import AutoRenewTypeEnum = Meevo.Model.AutoRenewTypeEnum;
    import PaymentAccountTypeEnum = Meevo.Model.PaymentAccountTypeEnum;
    import BillingTypeEnum = Meevo.Model.BillingTypeEnum;

    interface IClientMembershipBill {
        amount: number,
        clientMembershipBillId: Guid,
        clientMembershipBillStatus: ClientMembershipBillStatusEnum,
        entityVersion
        isRenewed: boolean,
        isManualBill: boolean,
        locationId: number,
        paymentDate: Date,
        nextAttemptDateTime: Date,
        clientMembershipBillStatusText: string,
        buttonStatuses: IBillButtonStatuses,
        waivedDate?: Date,
        hasAccruedServices?: boolean,
        clientMembershipBillsIsRenewal: boolean,
        clientMembershipBillFeeType: ClientMembershipBillFeeTypeEnum,
        freezeSettingEnum?: FreezeSettingEnum,
        isFutureTerminatedBill?: boolean
        isScriptedBill?: boolean,
        isPastScriptedBill?: boolean,
        billPackages?: any[]
    }

    interface IClientMembershipBillHistory {
        clientMembershipBillId: Guid,
        clientMembershipBillStatusEnum: ClientMembershipBillStatusEnum,
        clientMembershipBillStatusEnumTranslate: string,
        paymentProcessorResponseEnum: ProcessorResponseCodeEnum,
        paymentProcessorResponseEnumTranslate: string,
        paymentProcessorResponseMessageKey: string,
        responseMessageTranslate: string,
        dateUpdated: Date,
        cardLastFour: number,
        isBillRefunded: boolean,
        isManuallyPaid: boolean,
        userName: string
    }

    interface IBillButtonStatuses {
        canManualPay: boolean,
        canWaive: boolean,
        canUnWaive: boolean,
        canDelete: boolean,
        canChange: boolean,
        canViewHistory: boolean
    }

    interface IInsertPayment {
        canInsertPaymentInputs: boolean,
        showInsertPaymentInputs: boolean,
        minDate?: Date,
        maxDate?: Date
        enrollmentDate?: Date
    }

    enum ClientMembershipBillFeeTypeEnum {
        FreezeFee = 30032,
        RecurringFee = 30033,
        RenewalFee = 30034
    }

    enum PaymentStatus {
        Suspended,
        PaidNotAccrued,
        PaidAccrued
    }

    /**
    * Section
    */
    export class PaymentsSection extends MembershipBaseSection {
        private context: MembershipManagerContext;
        private scheduleList: Array<IClientMembershipBill>;
        // Selected bill from table
        private selectedBill: IClientMembershipBill;
        private radioStatusBill: ClientMembershipBillStatusEnum = ClientMembershipBillStatusEnum.NotBilled;
        private daysList = _.range(1, 32);
        private showRadioStatusBill: boolean = false;
        private insertPayment: IInsertPayment = {
            canInsertPaymentInputs: false,
            showInsertPaymentInputs: false
        };
        private isBillingTypePayInFull: boolean = false;
        private isAutoRenewTypePayInFull: boolean = false;
        private isInDev: boolean = true;
        private today: Date;
        private canEditPaymentDayOfMonth: boolean = false;
        public sectionEndpoint = 'payments';

        private _clientMembershipBillStatus = {
            [ClientMembershipBillStatusEnum.Deleted]: 'LABEL_DELETED',
            [ClientMembershipBillStatusEnum.Error]: 'LABEL_ERROR',
            [ClientMembershipBillStatusEnum.Failed]: 'LABEL_FAILED',
            [ClientMembershipBillStatusEnum.NotBilled]: 'LABEL_NOT_BILLED',
            [ClientMembershipBillStatusEnum.Paid]: 'LABEL_PAID',
            [ClientMembershipBillStatusEnum.Pending]: 'LABEL_PENDING',
            [ClientMembershipBillStatusEnum.Suspended]: 'LABEL_SUSPENDED',
            [ClientMembershipBillStatusEnum.SentToTransworld]: 'LABEL_SENT_TO_TRANSWORLD',
            [ClientMembershipBillStatusEnum.WaivedAccrued]: 'LABEL_WAIVED_ACCRUED',
            [ClientMembershipBillStatusEnum.WaivedNotAccrued]: 'LABEL_WAIVED_NOT_ACCRUED',
            [ClientMembershipBillStatusEnum.Refund]: 'LABEL_REFUND',
        };
        private _processorResponseCode = {
            [ProcessorResponseCodeEnum.AuthorizationFailed]: 'LABEL_AUTHORIZATION_FAILED',
            [ProcessorResponseCodeEnum.BalanceNotAvailable]: 'LABEL_BALANCE_NOT_AVAILABLE',
            [ProcessorResponseCodeEnum.CallIssuer]: 'MESSAGE_CC_STATUS_CALL_ISSUER',
            [ProcessorResponseCodeEnum.CommunicationError]: 'LABEL_COMMUNICATION_ERROR',
            [ProcessorResponseCodeEnum.Decline]: 'MESSAGE_CC_STATUS_DECLINED',
            [ProcessorResponseCodeEnum.Default]: '-',
            [ProcessorResponseCodeEnum.Duplicate]: 'MESSAGE_CC_STATUS_DUPLICATE',
            [ProcessorResponseCodeEnum.DuplicateApproved]: 'LABEL_DUPLICATE_APPROVED',
            [ProcessorResponseCodeEnum.Error]: 'LABEL_ERROR',
            [ProcessorResponseCodeEnum.ExpiredCard]: 'MESSAGE_CC_STATUS_EXPIRED_CARD',
            [ProcessorResponseCodeEnum.HostError]: 'MESSAGE_CC_STATUS_HOSTERROR',
            [ProcessorResponseCodeEnum.InvalidAccount]: 'LABEL_INVALID_ACCOUNT',
            [ProcessorResponseCodeEnum.InvalidData]: 'LABEL_INVALID_DATA',
            [ProcessorResponseCodeEnum.InvalidRequest]: 'MESSAGE_CC_STATUS_INVALID_REQUEST',
            [ProcessorResponseCodeEnum.NotAllowed]: 'LABEL_NOT_ALLOWED',
            [ProcessorResponseCodeEnum.NotDefined]: 'LABEL_NOT_DEFINED',
            [ProcessorResponseCodeEnum.OutOfBalance]: 'LABEL_OUT_OF_BALANCE',
            [ProcessorResponseCodeEnum.PartialApproval]: 'MESSAGE_CC_STATUS_PARTIAL_APPROVED',
            [ProcessorResponseCodeEnum.PickUpCard]: 'LABEL_PICK_UP_CARD',
            [ProcessorResponseCodeEnum.Success]: 'MESSAGE_CC_STATUS_SUCCESS',
            [ProcessorResponseCodeEnum.Returned]: 'MESSAGE_RETURNED',
            [ProcessorResponseCodeEnum.Pending]: 'LABEL_PENDING',
            [ProcessorResponseCodeEnum.Queued]: 'MESSAGE_QUEUED',
            [ProcessorResponseCodeEnum.Unknown]: 'MESSAGE_UNKNOWN',
            [ProcessorResponseCodeEnum.Originated]: 'MESSAGE_ORIGINATED',
            [ProcessorResponseCodeEnum.Settled]: 'MESSAGE_SETTLED',
            [ProcessorResponseCodeEnum.MerchantAccountNotFound]: 'MESSAGE_MERCHANT_ACCOUNT_NOT_FOUND'
        };
        private isHomeLocation: boolean = true;
        public isPendingBill: boolean = false;
        public isPIFMembershipInRenewalTerm: boolean = false;
        protected securityKeys = Meevo.Components.Common.SecurityKeys;
        public editRecurringFeeHide: boolean = false;
        public editRenewalFeeHide: boolean = false;
        public canEditRecurringFee: boolean = false;
        public canEditRenewalFee: boolean = false;
        public membershipInfo: Meevo.Model.Membership;
        private backupRecurringFee: any;
        private backupRenewalFee: any;

        constructor(params) {
            super(params);
            this.context = params.context;
        }

        public sectionModelRetrieved(): void {
            super.sectionModelRetrieved();
            this.today = new Date();
            this.today.setHours(0, 0, 0, 0);
            this.selectedBill = <IClientMembershipBill>{};

            this.insertPayment.showInsertPaymentInputs = false;
            this.insertPayment.canInsertPaymentInputs = false;

            this.isBillingTypePayInFull = this.model['billingTypeEnum'] === Meevo.Model.BillingTypeEnum.PaidInFull;
            this.isAutoRenewTypePayInFull = this.model['autoRenewTypeEnum'] === Meevo.Model.AutoRenewTypeEnum.PaidInFull;

            // disable payment buttons for non-home location
            this.isHomeLocation = this.identityService.locationId === this.model.homeLocationId;

            this.backupRecurringFee = this.model.membershipMemberSettings[0].recurringFee;
            this.backupRenewalFee = this.model.membershipMemberSettings[0].monthlyRenewalFee;
        }

        get clientMembershipBillStatusEnum(): any {
            return ClientMembershipBillStatusEnum;
        }

        public clientMembershipBillStatusTranslate(bill: IClientMembershipBill): string {
            let translate = this._clientMembershipBillStatus[bill.clientMembershipBillStatus];

            if (bill.clientMembershipBillStatus === ClientMembershipBillStatusEnum.WaivedAccrued
                && bill.hasAccruedServices === false) {
                translate = this._clientMembershipBillStatus[ClientMembershipBillStatusEnum.WaivedNotAccrued];
            }

            return translate;
        }

        /**
         * Load additional data for every section. Override method in membershipManager.base.section
         * */
        public loadDependentData(): ng.IPromise<any> {

            // Check canEditPaymentDayOfMonth
            this.canEditPaymentDayOfMonth = false;

            if (this.model.clientMembershipIndicatorStatus !== ClientMembershipIndicatorStatusEnum.Frozen
                && this.model.clientMembershipIndicatorStatus !== ClientMembershipIndicatorStatusEnum.InCancellation
                && this.model.clientMembershipIndicatorStatus !== ClientMembershipIndicatorStatusEnum.Suspended
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Expired
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Cancelled
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.CancelBeforeEnrollment
                && this.model.autoRenewTypeEnum !== AutoRenewTypeEnum.PaidInFull
                && !((this.model.autoRenewTypeEnum === AutoRenewTypeEnum.No || !this.model.autoRenewEnabled) && this.model.billingTypeEnum === BillingTypeEnum.PaidInFull)
                && !this.model.freezeStartDate
            ) {
                this.canEditPaymentDayOfMonth = true;
            }

            // TODO: add rules from story 12075
            this.context['securityChecker'].checkSecurityKey(this.securityKeys.MEMBERSHIP_MEMBERSHIPMANAGER_ACTION_EDITRECURRINGFEE)
                .then((securityCode: Meevo.Model.SecurityAccessEnum) => {
                    this.editRecurringFeeHide = securityCode === Meevo.Model.SecurityAccessEnum.Hide;
                    this.canEditRecurringFee = this.editRecurringFeeHide ? false : true;
                });
            this.context['securityChecker'].checkSecurityKey(this.securityKeys.MEMBERSHIP_MEMBERSHIPMANAGER_ACTION_EDITRENEWALFEE)
                .then((securityCode: Meevo.Model.SecurityAccessEnum) => {
                    this.editRenewalFeeHide = securityCode === Meevo.Model.SecurityAccessEnum.Hide;
                    this.canEditRenewalFee = this.editRenewalFeeHide ? false : true;
                });

            this.params.busyService.start();

            return this.params.$q.all([
                this.dataService.getListPaged('Membership/Client/Payment', <any>{ id: this.model.id })
                    .then((data: Array<IClientMembershipBill>) => {
                        this.scheduleList = data;
                        if (this.model.billingTypeEnum === Meevo.Model.BillingTypeEnum.PaidInFull && this.model.autoRenewTypeEnum === AutoRenewTypeEnum.Monthly) {
                            let renewalBills = _.filter(this.scheduleList, (item) => { return !item.isManualBill && item.clientMembershipBillFeeType === ClientMembershipBillFeeTypeEnum.RenewalFee });
                            this.isPIFMembershipInRenewalTerm = renewalBills.length > 0 ? this.today >= renewalBills[renewalBills.length - 1].paymentDate : false;
                        }

                        this.canEditPaymentDayOfMonth = this.canEditPaymentDayOfMonth && ((this.model.billingTypeEnum === BillingTypeEnum.PaidInFull && this.isPIFMembershipInRenewalTerm) ||
                            this.model.billingTypeEnum === BillingTypeEnum.RecurringPayments);

                        _.each(this.scheduleList, (item: IClientMembershipBill) => {
                            item.clientMembershipBillStatusText = this.clientMembershipBillStatusTranslate(item);
                            item.buttonStatuses = this.getBillButtonStatuses(item);

                            if (item.clientMembershipBillFeeType === ClientMembershipBillFeeTypeEnum.RenewalFee) {
                                item.clientMembershipBillsIsRenewal = true;
                            }

                            item.isFutureTerminatedBill = false;
                            if (this.model.terminationDate && item.clientMembershipBillStatus === ClientMembershipBillStatusEnum.NotBilled) {
                                item.isFutureTerminatedBill = this.model.terminationDate <= item.paymentDate;
                            }

                        });

                        this.isShowPaymentInputs();


                    }),

                this.loadMembershipInfo(),

                this.params.busyService.stop()
            ]);

            
        }

        public loadMembershipInfo(): ng.IPromise<any> {
            return this.dataService.getItem('membership', this.model.membershipId)
                .then((data: Meevo.Model.Membership | any) => {
                    this.membershipInfo = data;
                })
        }

        public editPaymentDayOfMonth() {
            if (this.model.freezeStartDate) {
                this.params.modalService.alert("MESSAGE_UNFREEZE_MEMBERSHIP_TO_CHANGE_PDOM");
            }
            else if (this.canEditPaymentDayOfMonth) {
                this.setModeEditing();
            }
        }

        public editMembershipFees(): void {
            if (this.canEditRecurringFee || this.canEditRenewalFee) {
                if (this.membershipInfo.isEmployeeMembership) {
                    this.params.modalService.warning('WARNING_CANNOT_EDIT_FEES_FOR_EMPLOYEE_MEMBERSHIP');
                } else {
                    if (this.model.paymentAccounts.length < 1) {
                        this.params.modalService.alert("MESSAGE_MEMBERSHIPMANAGER_AUTORENEW_NEED_PAYMENT").then(() => {
                            // GO to CC/ACH tab
                            this.params['context'].selectSection(this.params['context'].sections[1]);
                            this.setModeEditing();
                        });
                    } else {
                        this.setModeEditing();
                    }
                }
            }
        }

        public changeReccuringOrRenewalFee(isRenewalFee: boolean = false): void {
            let currentRecurringFee = this.model.membershipMemberSettings[0].recurringFee,
                currentRenewalFee = this.model.membershipMemberSettings[0].monthlyRenewalFee,
                minRecurringFee = this.membershipInfo.membershipMemberSettings[0].minRecurringFee,
                maxRecurringFee = this.membershipInfo.membershipMemberSettings[0].recurringFee,
                minRenewalFee = this.membershipInfo.membershipMemberSettings[0].minMonthlyRenewalFee,
                maxRenewalFee = this.membershipInfo.membershipMemberSettings[0].monthlyRenewalFee;

            if (this.model.billingTypeEnum === BillingTypeEnum.RecurringPayments && currentRecurringFee < minRecurringFee) {
                this.model.membershipMemberSettings[0].recurringFee = this.backupRecurringFee
                this.compareFeesMessage('recurring', true);
            }

            if (this.model.billingTypeEnum === BillingTypeEnum.RecurringPayments && currentRecurringFee > maxRecurringFee) {
                this.model.membershipMemberSettings[0].recurringFee = this.backupRecurringFee
                this.compareFeesMessage('recurring');
            }

            if (currentRenewalFee < minRenewalFee) {
                this.model.membershipMemberSettings[0].monthlyRenewalFee = this.backupRenewalFee;
                this.compareFeesMessage('renewal', true);
            }

            if (currentRenewalFee > maxRenewalFee) {
                this.model.membershipMemberSettings[0].monthlyRenewalFee = this.backupRenewalFee;
                this.compareFeesMessage('renewal');
            }

            if (isRenewalFee && this.model['feeGrandfatherSnapshotPoint']) {
                this.params.modalService.warning('WARNING_GRANDFATHERED_MEMBERSHIP_CHANGE_RENEWAL_FEE');
            }
        }

        public compareFeesMessage(fee: string, isLess: boolean = false): ng.IPromise<any> {
            let message;

            if (isLess) {
                message = this.membershipInfo.isOwner ? this.params.$translate.instant('LABEL_FEE_CANNOT_BE_LESS_OF_DEFINED_DEFINITION', { fee: fee }) : this.params.$translate.instant('LABEL_FEE_CANNOT_BE_LESS_OF_DEFINED_MARKET', { fee: fee });
            } else {
                message = this.membershipInfo.isOwner ? this.params.$translate.instant('LABEL_FEE_CANNOT_BE_GREATER_OF_DEFINED_DEFINITION', { fee: fee }) : this.params.$translate.instant('LABEL_FEE_CANNOT_BE_GREATER_OF_DEFINED_MARKET', { fee: fee });
            }

            return this.params.modalService.alert(message);
        }

        public setModeEditing() {
            if (!this.context.isEditing) {
                this.context.edit();
            }
        }

        // Statuses for button work 
        private getBillButtonStatuses(item: IClientMembershipBill): IBillButtonStatuses {
            return {
                canManualPay: this.isManualPayStatus(item),
                canWaive: this.isShowWaiveStatus(item),
                canUnWaive: this.isShowUnWaiveStatus(item),
                canDelete: this.isShowDeleteStatus(item),
                canChange: this.isShowChangeStatus(item),
                canViewHistory: this.isShowHistoryBill(item)
            }
        }

        // Status for Manual Pay button
        private isManualPayStatus(item: IClientMembershipBill): boolean {
            if (item.clientMembershipBillStatus
                && item.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.Paid
                && item.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.Deleted
                && item.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.WaivedAccrued
                && item.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.Refund
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Cancelled
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.CancelBeforeEnrollment
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Expired
                && item.locationId === this.model.homeLocationId
            ) {
                return true;
            }
            return false;
        }

        // Status for Waive button
        private isShowWaiveStatus(item: IClientMembershipBill): boolean {
            let isPaymentDateEqualToday = moment(item.paymentDate).isSame(this.today);
            if (
                (this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Cancelled
                    && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.CancelBeforeEnrollment
                    && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Expired
                    && item.locationId === this.model.homeLocationId
                )
                &&
                (
                    // Past suspended payment
                    ((item.clientMembershipBillStatus === ClientMembershipBillStatusEnum.Suspended
                        && this.model.clientMembershipIndicatorStatus !== ClientMembershipIndicatorStatusEnum.InCancellation
                    ) && !isPaymentDateEqualToday)
                    // future payment marked as not billed
                    || item.clientMembershipBillStatus === ClientMembershipBillStatusEnum.NotBilled
                    || (item.clientMembershipBillStatus === ClientMembershipBillStatusEnum.Error && !isPaymentDateEqualToday)
                )
            ) {
                return true;
            }
            return false;
        }

        // Status for UnWaive button
        private isShowUnWaiveStatus(item: IClientMembershipBill): boolean {
            if (item.clientMembershipBillStatus
                && item.clientMembershipBillStatus === ClientMembershipBillStatusEnum.WaivedAccrued
                && item.locationId === this.model.homeLocationId
            ) {
                return true;
            }
            return false;
        }

        // Status for Payment Inputs button
        private isShowPaymentInputs(): void {
            let scheduleLast = _.max(this.scheduleList, function (item) {
                return item.paymentDate.getUTCMilliseconds();
            });

            if (scheduleLast
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Cancelled
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.CancelBeforeEnrollment
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Expired
                && this.model.clientMembershipIndicatorStatus !== ClientMembershipIndicatorStatusEnum.Frozen
            ) {
                this.insertPayment.canInsertPaymentInputs = true;
                // 86400000 millisecond = 1 day
                this.insertPayment.minDate = new Date(new Date().getTime() + 86400000); // set tomorrow
                this.insertPayment.maxDate = new Date(scheduleLast.paymentDate.getTime() - 86400000); // day before paymentDate
                this.insertPayment.enrollmentDate = this.insertPayment.minDate;
            }
        }

        // Status for Delete button
        private isShowDeleteStatus(item): boolean {
            if (item.clientMembershipBillStatus
                && item.clientMembershipBillStatus === ClientMembershipBillStatusEnum.NotBilled
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.CancelBeforeEnrollment
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Cancelled
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Expired
                && item.locationId === this.model.homeLocationId
                && item.isManualBill
            ) {
                return true;
            }
            return false;
        }

        // Status for Change button
        private isShowChangeStatus(item: IClientMembershipBill): boolean {
            if (item.clientMembershipBillStatus
                && item.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.Deleted
                && item.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.Refund
                && item.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.WaivedAccrued
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Cancelled
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.CancelBeforeEnrollment
                && this.model.clientMembershipStatusEnum !== ClientMembershipStatusEnum.Expired
                && item.locationId === this.model.homeLocationId
            ) {
                return true;
            }
            return false;
        }

        // Modal "change bill status"
        public changeBillStatus(item: IClientMembershipBill): void {

            this.dataService.customMethodData(`${this.appSettings.API_ROOT}/membership/associatedBill/${item.clientMembershipBillId}`, 'GET', null)
                .then((res) => {
                    let associatedBillsList = res.data.associatedBills,
                        paymentRequestId = res.data.paymentRequestId;

                    const options: Components.Modal.IModalOptions = {
                        templateUrl: 'app/plugins/membershipManager/sectionViews/changeBillStatusModalTemplate.html',
                        clickOutsideToClose: false,
                        locals: {
                            modalTitle: 'LABEL_PAYMENT_CHANGE_STATUS',
                            associatedBillsList: associatedBillsList,
                            paymentRequestId: paymentRequestId
                        },
                        controller: 'meevo.plugins.membershipManager.ChangeBillStatusModalController',
                        controllerAs: 'vm'
                    };

                    if (paymentRequestId) {
                        if (res.data.accountTypeEnum === PaymentAccountTypeEnum.Card) {
                            this.params.modalService.showCustom(options).then((res: any) => {
                                this.callChangeStatusModal(res.status, item.clientMembershipBillId, paymentRequestId);
                            });
                        } else {
                            if (res.data.externalTransactionId) {
                                this.params.modalService.showCustom(options).then((res: any) => {
                                    this.callChangeStatusModal(res.status, item.clientMembershipBillId, paymentRequestId);
                                });
                            } else {
                                this.params.modalService.alert('MESSAGE_PENDING_ALERT_NO_EXTERNAL_TRANSACTION_ID');
                            }
                        }
                    } else {
                        this.params.modalService.showCustom(options).then((res: any) => {
                            this.callChangeStatusModal(res.status, item.clientMembershipBillId);
                        });
                    }
                });
        }

        // Reason modal
        public callReasonModal(title: string, label: string): angular.IPromise<any> {
            const options: Components.Modal.IModalOptions = {
                templateUrl: 'app/plugins/membershipManager/sectionViews/reasonModalTemplate.html',
                clickOutsideToClose: false,
                locals: {
                    titleTranslate: title,
                    labelTranslate: label
                },
                controller: 'meevo.plugins.membershipManager.ReasonModalController',
                controllerAs: 'vm'
            };
            return this.params.modalService.showCustom(options).then((res: any) => {
                return res;
            });
        }

        // Call Change Paid/Suspended status modal
        public callChangeStatusModal(status: boolean, billId: Guid, paymentRequestId?: string): void {
            let criteria: any = [];

            criteria.clientMembershipBillId = billId;
            criteria.paymentRequestId = paymentRequestId || null;

            if (status) {
                this.callReasonModal('LABEL_CHANGE_STATUS_TO_PAID', 'LABEL_CHANGE_STATUS_TO_PAID_REASON').then((result: any) => {
                    if (result.status) {
                        criteria.reason = result.message;

                        this.params.modalService.confirmHandler('MESSAGE_CHANGE_STATUS_ACCRUALS_PAID')
                            .then((res: boolean) => {
                                criteria.status = res ? PaymentStatus.PaidAccrued : PaymentStatus.PaidNotAccrued;

                                this.dataService.patch('membership/payments/status', criteria).then(() => {
                                    this.loadDependentData();
                                });
                            });
                    }
                });
            } else if (!status) {
                this.callReasonModal('LABEL_CHANGE_STATUS_TO_SUSPENDED', 'LABEL_CHANGE_STATUS_TO_SUSPENDED_REASON').then((result: any) => {
                    if (result.status) {
                        criteria.status = PaymentStatus.Suspended;
                        criteria.reason = result.message;

                        this.dataService.patch('membership/payments/status', criteria).then(() => {
                            this.loadDependentData();
                        });
                    }
                });
            }
        }

        // Status for History button
        private isShowHistoryBill(item): boolean {
            if (item) {
                return true;
            }
            return false;
        }

        // Popup bill history
        public viewHistoryBill(item: IClientMembershipBill): ng.IPromise<any> {

            return this.dataService.getListPaged('Membership/Client/Payment', <any>{ id: item.clientMembershipBillId }, item.clientMembershipBillId, 'History')
                .then((data: Array<IClientMembershipBillHistory>) => {

                    let billHistory = data;

                    _.each(billHistory, (e: IClientMembershipBillHistory) => {
                        e.clientMembershipBillStatusEnumTranslate = this._clientMembershipBillStatus[e.clientMembershipBillStatusEnum];
                        e.paymentProcessorResponseEnumTranslate = this._processorResponseCode[e.paymentProcessorResponseEnum];
                        if (e.clientMembershipBillStatusEnum == ClientMembershipBillStatusEnum.Refund) {
                            e.isBillRefunded = true;
                        }

                        if (e.isManuallyPaid) {

                            if (e.paymentProcessorResponseMessageKey) {
                                e.responseMessageTranslate = e.paymentProcessorResponseMessageKey;
                            } else if (e.paymentProcessorResponseEnumTranslate) {
                                e.responseMessageTranslate = e.paymentProcessorResponseEnumTranslate;
                            } else {
                                e.responseMessageTranslate = "LABEL_GENERIC_NOT_AVAILABLE";
                            }

                        } else {
                            e.responseMessageTranslate = e.paymentProcessorResponseMessageKey ? e.paymentProcessorResponseMessageKey : e.paymentProcessorResponseEnumTranslate;
                        }

                    });

                    // Initialize modal history status
                    const options: Components.Modal.IModalOptions = {
                        templateUrl: 'app/plugins/membershipManager/sectionViews/billHistoryModalTemplate.html',
                        clickOutsideToClose: true,
                        locals: {
                            modalTitle: 'LABEL_PAYMENT_HISTORY_FOR',
                            modalDate: item.paymentDate,
                            nextAttemptDate: item.nextAttemptDateTime,
                            billHistory: billHistory,
                            isSingleLocation: this.identityService.current().isSingleLocation
                        },
                        controller: 'meevo.plugins.membershipManager.BillHistoryModalController',
                        controllerAs: 'vm'
                    };
                    this.params.modalService.showCustom(options)
                        .then((response: any) => { });
                });
        }

        // Select bill from table
        public selectBill(item): void {
            this.selectedBill = item;
            this.showRadioStatusBill = false;
            this.radioStatusBill = this.isShowChangeStatus(item) ? item.clientMembershipBillStatus : ClientMembershipBillStatusEnum.NotBilled;

            if (item.clientMembershipBillStatus === ClientMembershipBillStatusEnum.Pending) {
                let currentDate = new Date(),
                    diffDate = moment(item.processingDate).diff(moment(currentDate), 'days');

                if (diffDate <= -14) {
                    this.isPendingBill = true;
                    item.buttonStatuses.canManualPay = true;
                } else {
                    item.buttonStatuses.canChange = false;
                    item.buttonStatuses.canManualPay = false;
                }

            } else {
                this.isPendingBill = false;
                item.buttonStatuses.canChange = false;
            }

        }

        // change status bill function
        public editStatusBill(status?: ClientMembershipBillStatusEnum, message?: string, modelOption?: any, bill?: any): angular.IPromise<any> {
            let entity = {
                EntityVersion: this.selectedBill.entityVersion,
                Model: {
                    clientMembershipId: this.model.id,
                    clientMembershipBillStatus: status ? status : undefined,
                    note: message ? message : this._clientMembershipBillStatus[status]
                }
            };

            if (!bill) {
                bill = this.selectedBill;
            }

            if (modelOption) {
                _.extend(entity.Model, modelOption);
            }
            this.params.busyService.start();

            return this.dataService.patch('Membership/Client/Payment', entity, bill.clientMembershipBillId)
                .then((data: any) => {
                    this.params.busyService.stop();

                    if (data.isSuccessful) {
                        // Update bill
                        _.extend(bill, data.bill);
                        bill.clientMembershipBillStatusText = this.clientMembershipBillStatusTranslate(bill);
                        bill.buttonStatuses = this.getBillButtonStatuses(bill);
                        bill.billPackages = data.billPackages;
                        // Update all list
                        this.loadDependentData();

                        // Update membership indicator
                        //this.context.updateMembershipIndicator();

                        // Update Membership after status changed
                        this.dataService.customMethodData(`${this.appSettings.API_ROOT}/membership/client/${this.model.id}`, 'GET', null)
                            .then((res) => {
                                this.model = res.data;
                            });
                    }
                    return data;
                });
        }

        // Change to delete status bill
        public deleteStatusBill(): void {

            // confirm
            this.params.modalService.warning('MESSAGE_DELETE_PAYMENT_CONFIRMATION', Meevo.Components.Modal.ModalTranslationModes.Translate, false, { showCancelButton: true, cancelButtonContentKey: 'LABEL_NO', cancelButtonHotKey: 'MODAL_NO_BUTTON', okButtonHotKey: 'MODAL_YES_BUTTON', okButtonContentKey: 'LABEL_YES' })
                .then((res: boolean) => {
                    if (res === true) {
                        // Initialize modal for reason
                        this.callReasonModal('LABEL_DELETING_PAYMENT', 'LABEL_DELETING_PAYMENT_REASON').then((response: any) => {
                            if (response.status === true) {
                                this.editStatusBill(ClientMembershipBillStatusEnum.Deleted, response.message);
                            }
                        });
                    }
                });
        }

        // Change to waive status bill
        public waiveStatusBill(): void {

            // confirm
            this.params.modalService.alert('MESSAGE_WAIVE_PAYMENT_CONFIRMATION', Meevo.Components.Modal.ModalTranslationModes.Translate, false, { showCancelButton: true, cancelButtonContentKey: 'LABEL_NO', cancelButtonHotKey: 'MODAL_NO_BUTTON', okButtonHotKey: 'MODAL_YES_BUTTON', okButtonContentKey: 'LABEL_YES' })
                .then((res1: boolean) => {
                    if (res1 === true) {

                        // Initialize modal for reason
                        this.callReasonModal('LABEL_WAIVE_PAYMENT', 'LABEL_WAIVE_PAYMENT_REASON').then((response: any) => {
                            if (response.status === true) {

                                // confirm
                                this.params.modalService.confirmHandler('LABEL_WAIVE_PAYMENT_ASSOCIATED_ACCRUED_SERVICE', Meevo.Components.Modal.ModalTranslationModes.Translate)
                                    .then((res: boolean) => {
                                        this.selectedBill.waivedDate = this.today;
                                        let accrueServices;

                                        if (res === true) {
                                            this.selectedBill.hasAccruedServices = true;
                                            accrueServices = true;
                                        } else {
                                            accrueServices = false;
                                        }
                                        this.editStatusBill(null, response.message, { isWaive: true, accrueServices: accrueServices }).then(() => {
                                            this.selectedBill = undefined;
                                        });
                                    });

                            }
                        });
                    }
                });
        }

        // Do unwaive bill
        public unwaiveStatusBill(): void {

            // Can only unwaive a payment on the same day it was waived.
            if (this.selectedBill.waivedDate < this.today) {
                this.params.modalService.alert('MESSAGE_UNWAIVE_PAYMENT_ONLY_THE_SAME_DAY_IT_WAS_WAIVED', Meevo.Components.Modal.ModalTranslationModes.Translate)
            } else {
                // confirm to unwaive
                this.params.modalService.confirmHandler('MESSAGE_UNWAIVE_PAYMENT_ARE_YOU_SURE', Meevo.Components.Modal.ModalTranslationModes.Translate)
                    .then((res: boolean) => {
                        if (res === true) {
                            this.editStatusBill(null, null, { isWaive: false }).then(() => {
                                this.selectedBill = undefined;
                            });
                        }
                    });
            }
        }

        public showInsertPayment(): void {
            let scheduleLast = _.max(this.scheduleList, function (item) {
                return item.paymentDate.getUTCMilliseconds();
                }),
                maxDate = new Date(scheduleLast.paymentDate.getTime() - 86400000),
                minDate = new Date(new Date().getTime() + 86400000);

            if (minDate > maxDate) {
                this.params.modalService.alert('MESSAGE_MEMBERSHIP_INSERT_PAYMENT_WRONG_DATE');
                return;
            }

            this.insertPayment.showInsertPaymentInputs = !this.insertPayment.showInsertPaymentInputs;
            this.setModeEditing();
            this.showRadioStatusBill = false;
        }

        // Insert payment 
        public insertPaymentBill(): angular.IPromise<any> {

            // Initialize modal for reason delete
            return this.callReasonModal('LABEL_INSERT_PAYMENT', 'LABEL_INSERT_PAYMENT_REASON').then((response: any) => {
                let request: any;

                if (response.status === true) {
                    request = {
                        clientMembershipId: this.model.id,
                        paymentDate: this.insertPayment.enrollmentDate,
                        reason: response.message,
                    };

                    // API call for insert payment
                    this.dataService.customMethodData('api/Membership/Client/Payment', 'POST', request)
                        .then((data: any) => {
                            this.loadDependentData();
                            this.insertPayment.showInsertPaymentInputs = false;
                        });
                }
            });
        }

        // Do Manual Pay
        public manualPayBill(): void {
            let vm = this;
            let prevBills: Array<IClientMembershipBill> = this.scheduleList.filter((bill: IClientMembershipBill, index: number) => {
                if (!this.selectedBill.isPastScriptedBill
                    && bill.paymentDate < this.selectedBill.paymentDate
                    && (bill.clientMembershipBillStatus === ClientMembershipBillStatusEnum.Error
                        || bill.clientMembershipBillStatus === ClientMembershipBillStatusEnum.NotBilled
                        || bill.clientMembershipBillStatus === ClientMembershipBillStatusEnum.Suspended)
                    && !bill.isPastScriptedBill
                ) {
                    return true;
                }
            });

            prevBills.push(this.selectedBill);

            if (prevBills.length > 1) {

                // Initialize modal show list prev bills
                const options: Components.Modal.IModalOptions = {
                    templateUrl: 'app/plugins/membershipManager/sectionViews/manualPayBillModalTemplate.html',
                    clickOutsideToClose: true,
                    locals: {
                        modalTitle: 'MESSAGE_MEMBERSHIPMANAGER_PAYMENTS_CURRENTLY_PAST_DUE',
                        modalSubTitle: 'MESSAGE_MEMBERSHIPMANAGER_PAY_ALL_ABOVE_PAYMENTS',
                        bills: prevBills,
                    },
                    controller: 'meevo.plugins.membershipManager.ManualPayBillModalController',
                    controllerAs: 'vm'
                };
                this.params.modalService.showCustom(options)
                    .then((response: boolean) => {
                        if (response === true) {
                            this.manualPaySequence(prevBills);
                        }
                    });
            } else {

                if (prevBills.length === 0 && this.selectedBill.isScriptedBill) {
                    prevBills.push(this.selectedBill);
                }

                this.params.modalService.confirmHandler('MESSAGE_MEMBERSHIPMANAGER_CONFIRM_MANUAL_PAY', Meevo.Components.Modal.ModalTranslationModes.Translate)
                    .then((res: boolean) => {
                        if (res === true) {
                            this.manualPaySequence(prevBills);
                        }
                    });
            }

        }

        // Create sequence for update all bill on the server
        // bills - prev not payed bills
        private manualPaySequence(bills: Array<IClientMembershipBill>): void {
            let sequence = this.manualPayChangeBill(bills[0]);

            // loop without last item - selectedBill
            for (let i = 1; i < bills.length; i++) {
                sequence = sequence.then(() => {
                    return this.manualPayChangeBill(bills[i]);
                });
            }

            sequence.then(() => {
                let billsToPay = _.map(bills, (el: IClientMembershipBill) => {
                    return {
                        clientMembershipBillId: el.clientMembershipBillId,
                        amount: el.amount,
                        paymentDate: el.paymentDate,
                        clientMembershipBillFeeType: el.clientMembershipBillFeeType,
                        freezeSetting: el.freezeSettingEnum,
                        billPackages: el.billPackages
                    }
                });

                // Go to register with payment bills
                let stateParams = {
                    action: Meevo.Components.Common.Messages.MANUAL_BILL_PAYMENT,
                    section: "SECTION_MEMBERSHIPMANAGER_PAYMENTS",
                    clientMembership: this.model,
                    clientMembershipBills: billsToPay
                };

                this.billGoToRegister(stateParams)
            });
        }

        // Update bill before manual Pay
        private manualPayChangeBill(item): angular.IPromise<any> {
            let entity = {
                clientMembershipId: this.model.id,
                accrueServices: true,
                isManualPay: true
            };
            return this.editStatusBill(null, null, entity, item);
        }

        // Refund payment
        public refundBill(): void {

            // confirm
            this.params.modalService.confirmHandler('MESSAGE_REFUND_PAYMENT_CONFIRMATION', Meevo.Components.Modal.ModalTranslationModes.Translate)
                .then((res: boolean) => {
                    if (res === true) {
                        this.editStatusBill(null, null, { isRefund: true }).then((data) => {
                            if (!data.isSuccesful) {
                                let bill = {
                                    clientMembershipBillId: this.selectedBill.clientMembershipBillId,
                                    amount: data.refundModel.amountForRefund,
                                    paymentDate: data.bill.paymentDate,
                                    saleDate: data.refundModel.saleDate,
                                    saleId: data.refundModel.saleId,
                                    saleLineId: data.refundModel.saleLineId,
                                    saleLocationId: data.refundModel.saleLocationId,
                                    clientMembershipBillFeeType: this.selectedBill.clientMembershipBillFeeType
                                };

                                let stateParams = {
                                    action: Meevo.Components.Common.Messages.MANUAL_BILL_PAYMENT_REFUND,
                                    section: "SECTION_MEMBERSHIPMANAGER_PAYMENTS",
                                    clientMembership: this.model,
                                    clientMembershipBills: [bill],
                                };

                                // go to register
                                this.billGoToRegister(stateParams);
                            }
                        });
                    }
                });
        }

        // pass user to register
        private billGoToRegister(stateParams): void {

            // TODO Set right stateParams. 
            // Waiting team 1. Which parameters to pass
            this.params.$rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                stateName: "register",
                stateParams: stateParams
            });
        }

        public dateSorting = false;
        public toggleDateSorting(): any {
            this.dateSorting = !this.dateSorting;
        }

    }
}